/**
 * Auth_Service.ts
 * API functions for Platform auth endpoints.
 *
 * Cookies (accessToken + refreshToken) are httpOnly — never touched here.
 * Only non-sensitive UI data (orgId) is stored via Token_Handler.
 */

import axiosInstance from '../../api/axiosInstance';
import { Token_Handler } from '../../storage/tokenHandler';
import {
  Platform_Signup_Url,
  Platform_Me_Url,
  Platform_Logout_Url,
  Platform_Forgot_Password_Url,
  Platform_Reset_Password_Url,
  Platform_Verify_Email_Url,
  Platform_Resend_Verification_Url,
  Platform_Switch_Org_Url,
  Platform_Login_Mfa_Url,
} from '../../api/endpoints';
import { SignupPayload, SignupResponse } from '../../schemas/auth/signup';

// The platform mints product-scoped JWTs; SK Catalog always signs up / recovers
// against the 'catalog' product so the issued token carries catalog scopes.
const PRODUCT = 'catalog' as const;

/* ─────────────────────────────────────────
   Signup — POST /v1/auth/signup
───────────────────────────────────────── */

export const signupOrg = async (payload: SignupPayload): Promise<SignupResponse> => {
  const { data } = await axiosInstance.post<SignupResponse>(Platform_Signup_Url, {
    product: PRODUCT,
    ...payload,
  });

  await Promise.all([
    data?.data?.org?.id          && Token_Handler.setOrgId(data.data.org.id),
    data?.data?.org?.name        && Token_Handler.setBusinessName(data.data.org.name),
    data?.data?.user?.first_name && Token_Handler.setFirstName(data.data.user.first_name),
    data?.data?.user?.last_name  && Token_Handler.setLastName(data.data.user.last_name),
  ].filter(Boolean));

  return data;
};

/* ─────────────────────────────────────────
   Me — GET /v1/me
   Module-level cache (5s TTL) + in-flight deduplicator so parallel callers
   (AuthContext, App_Navigator) share one request.
───────────────────────────────────────── */

const ME_TTL = 5_000;
let _meCache: { data: any; at: number } | null = null;
let _meInflight: Promise<any> | null = null;

export const invalidateMeCache = () => { _meCache = null; };

export const getCurrentUser = async (): Promise<any> => {
  if (_meCache && Date.now() - _meCache.at < ME_TTL) return _meCache.data;
  if (!_meInflight) {
    _meInflight = axiosInstance
      .get(Platform_Me_Url)
      .then(r => { _meCache = { data: r.data, at: Date.now() }; return r.data; })
      .finally(() => { _meInflight = null; });
  }
  return _meInflight;
};

/* ─────────────────────────────────────────
   Logout — POST /v1/auth/logout
───────────────────────────────────────── */

export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
    await Token_Handler.clearAll();
  }
};

/* ─────────────────────────────────────────
   Forgot Password — POST /v1/auth/forgot-password
───────────────────────────────────────── */

export interface ForgotPasswordResponse {
  message: string;
}

export const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
  // Platform returns 204 (no body) on success to prevent enumeration —
  // axios then leaves `data` as an empty string. Synthesize a friendly
  // response so screens can always read `res.message` without a crash.
  const { data } = await axiosInstance.post<ForgotPasswordResponse | ''>(
    Platform_Forgot_Password_Url,
    { email, product: PRODUCT },
  );
  return data && typeof data === 'object' && 'message' in data
    ? (data as ForgotPasswordResponse)
    : { message: 'If an account exists for that email, a reset link has been sent.' };
};

/* ─────────────────────────────────────────
   Reset Password — POST /v1/auth/reset-password
───────────────────────────────────────── */

export interface ResetPasswordResponse {
  message: string;
}

export const resetPassword = async (token: string, newPassword: string): Promise<ResetPasswordResponse> => {
  // API expects snake_case: new_password (not newPassword).
  // Platform returns 204 on success — handle the empty body the same way
  // forgotPassword does so the caller can always destructure a message.
  const { data } = await axiosInstance.post<ResetPasswordResponse | ''>(
    Platform_Reset_Password_Url,
    { token, new_password: newPassword },
  );
  return data && typeof data === 'object' && 'message' in data
    ? (data as ResetPasswordResponse)
    : { message: 'Your password has been reset. Please sign in.' };
};

/* ─────────────────────────────────────────
   Verify Email — POST /v1/auth/verify-email
   Token arrives via the emailed link; flips the org ACTIVE.
───────────────────────────────────────── */

export interface VerifyEmailResponse {
  data: { verified: boolean; user_id: string; activated_org_ids: string[] };
}

export const verifyEmail = async (token: string): Promise<VerifyEmailResponse> => {
  const { data } = await axiosInstance.post<VerifyEmailResponse>(Platform_Verify_Email_Url, { token });
  invalidateMeCache();
  return data;
};

/* ─────────────────────────────────────────
   Resend Verification — POST /v1/auth/resend-verification
   AUTHENTICATED: the platform resends to the *logged-in session's own user*
   (no email argument — no enumeration surface). The caller must already hold
   a valid session cookie, which an unverified login does establish. Scoped to
   `product: 'catalog'` so the emailed link returns to this SPA (?mode=verify).
   Already-verified users are no-op'd server-side with a 204.
───────────────────────────────────────── */

export interface ResendVerificationResponse {
  message: string;
}

export const resendVerification = async (): Promise<ResendVerificationResponse> => {
  // Platform returns 204 (empty body) on success — synthesize a friendly
  // message so screens can always read `res.message`, matching forgotPassword.
  const { data } = await axiosInstance.post<ResendVerificationResponse | ''>(
    Platform_Resend_Verification_Url,
    { product: PRODUCT },
  );
  return data && typeof data === 'object' && 'message' in data
    ? (data as ResendVerificationResponse)
    : { message: 'Verification email sent. Please check your inbox.' };
};

/* ─────────────────────────────────────────
   Complete MFA challenge — POST /v1/auth/login/mfa
   Called after login returns { kind: 'mfa_required', mfa_token }.
───────────────────────────────────────── */

export const completeMfa = async (mfaToken: string, code: string): Promise<any> => {
  const { data } = await axiosInstance.post(Platform_Login_Mfa_Url, { mfa_token: mfaToken, code });
  invalidateMeCache();
  return data;
};

/* ─────────────────────────────────────────
   Switch active org — POST /v1/auth/switch-org
   Mints fresh cookies scoped to another org the user belongs to.
───────────────────────────────────────── */

export const switchOrg = async (orgId: string): Promise<any> => {
  const { data } = await axiosInstance.post(Platform_Switch_Org_Url, { org_id: orgId });
  invalidateMeCache();
  if (data?.org?.id) await Token_Handler.setOrgId(data.org.id);
  if (data?.data?.org?.id) await Token_Handler.setOrgId(data.data.org.id);
  return data;
};
